import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import { Header } from "../../components/Header";
import { HeaderRoleBadge } from "../../components/HeaderRoleBadge";
import { LanguageShortcutFab } from "../../components/LanguageShortcutFab";
import { UserActions } from "../../components/UserActions";
import { UserInfo } from "../../components/UserInfo";
import { EmailVerificationBanner } from "../../components/EmailVerificationBanner";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { faSignOutAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { logout } from "../../components/funtionsBD/Authservice";
import { openPingfloManual } from "../../tour/PingfloManual";
import { disconnectSocket } from "../../socket";
import { clearLocalStoragePreservingBrand } from "../../utils/localStorageBrand";
import { useBrand } from "../../context/BrandContext";
import {
  applyAppLanguage,
  preferredAppLanguage,
  normalizeAppLang,
  storedAppLangRaw
} from "../../utils/appLanguages";
import { ip_back } from "../../config";
import axios from "axios";
import "../../components/UserAction.css";

export const User = () => {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(!user);
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { themeMode } = useBrand();
  const navigate = useNavigate();
  const fetchedForId = useRef(null);

  const fetchUserProfile = async () => {
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "null");
      const token = stored?.user_token || user?.user_token;
      const userId = stored?.user_id || user?.user_id;
      const clubId = String(
        stored?.club_id || stored?.club_details?.club_id || stored?.club?.club_id || ""
      ).trim();
      if (!token) {
        setLoading(false);
        return;
      }

      const isFirstPaint = !user;
      if (isFirstPaint) setLoading(true);

      const response = await axios.get(`${ip_back}api/user/getProfile`, {
        headers: {
          Authorization: token,
          "user-id": userId,
          "Accept-Language": normalizeAppLang(i18n.language) || "en",
          "Content-Type": "application/json",
        },
        params: clubId ? { club_id: clubId } : undefined,
      });

      if (response.data.status === "success") {
        const freshUserData = response.data.data;
        const currentUser = JSON.parse(localStorage.getItem("user") || "null");
        if (currentUser && currentUser.isProfileCompleted === true) {
          freshUserData.isProfileCompleted = true;
        }
        if (currentUser?.user_token && !freshUserData.user_token) {
          freshUserData.user_token = currentUser.user_token;
        }

        localStorage.setItem("user", JSON.stringify(freshUserData));
        setUser(freshUserData);
        if (freshUserData.user_lang && !storedAppLangRaw()) {
          const freshLang = normalizeAppLang(freshUserData.user_lang);
          if (normalizeAppLang(i18n.language) !== freshLang) {
            applyAppLanguage(i18n, freshLang);
          }
        }
      }
    } catch (error) {
      const localStorageUser = JSON.parse(localStorage.getItem("user") || "null");
      if (localStorageUser) setUser(localStorageUser);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const targetLang = preferredAppLanguage(user, i18n);
    if (normalizeAppLang(i18n.language) !== targetLang) {
      applyAppLanguage(i18n, targetLang);
    }
  }, [user?.user_lang]);

  useEffect(() => {
    const userId = user?.user_id;
    if (!userId || fetchedForId.current === userId) return undefined;
    fetchedForId.current = userId;
    const timer = setTimeout(() => {
      fetchUserProfile();
    }, 400);
    return () => clearTimeout(timer);
  }, [user?.user_id]);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    const token = localStorage.getItem("token") || user?.user_token;
    try {
      if (token) await logout(token);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearLocalStoragePreservingBrand();
      sessionStorage.clear();
      disconnectSocket();
      navigate("/login");
    }
  };

  return (
    <>
      <div className="user_profile">
        <Header
          className="user-profile-header"
          userRole={user?.type_user_id}
          user={user}
          hideClubLogo
          hideRoleBadge
          element={
            user ? (
              <HeaderRoleBadge
                user={user}
                className="header-role-badge--profile"
              />
            ) : null
          }
          trailing={
            <div className="user-profile-header__trailing">
              <LanguageShortcutFab variant="inline" />
              <button
                type="button"
                className="user-profile-help-btn"
                onClick={() => openPingfloManual()}
                title={t("help", "Help")}
                aria-label={t("help", "Help")}
              >
                <HelpOutlineIcon sx={{ fontSize: 18 }} />
                <span>{t("help", "Help")}</span>
              </button>
              <button
                type="button"
                className="user-profile-logout-btn"
                onClick={() => setLogoutConfirmOpen(true)}
                disabled={loggingOut}
                title={t("logout")}
                aria-label={t("logout")}
              >
                <FontAwesomeIcon icon={faSignOutAlt} />
                <span>{t("logout")}</span>
              </button>
            </div>
          }
        />
        {loading || !user ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading profile data...</p>
          </div>
        ) : (
          <>
            <EmailVerificationBanner
              user={user}
              onVerified={() => fetchUserProfile()}
            />
            <UserInfo user={user} />
            <UserActions user={user} />
            {user?.type_user_id === 1 && <div className="admin_card" />}
          </>
        )}
      </div>

      <Dialog
        open={logoutConfirmOpen}
        onClose={loggingOut ? undefined : () => setLogoutConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        className={themeMode === "light" ? "light-mode" : "dark-mode"}
        PaperProps={{
          className: `user-profile-logout-dialog user-profile-logout-dialog--${
            themeMode === "light" ? "light" : "dark"
          }`,
          "aria-labelledby": "user-profile-logout-title",
        }}
      >
        <div className="user-profile-logout-confirm">
          <div className="user-profile-logout-confirm__icon" aria-hidden>
            <FontAwesomeIcon icon={faSignOutAlt} />
          </div>
          <h2 id="user-profile-logout-title">
            {t("confirmLogoutTitle", "Log out?")}
          </h2>
          <p>
            {t(
              "confirmLogoutMessage",
              "You will need to sign in again to access your club account."
            )}
          </p>
          <div className="user-profile-logout-confirm__actions">
            <button
              type="button"
              className="user-profile-logout-confirm__cancel"
              onClick={() => setLogoutConfirmOpen(false)}
              disabled={loggingOut}
            >
              {t("cancel", "Cancel")}
            </button>
            <button
              type="button"
              className="user-profile-logout-confirm__confirm"
              onClick={handleLogout}
              disabled={loggingOut}
            >
              {loggingOut
                ? t("loading", "Loading…")
                : t("logout", "Log Out")}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
